import { sameAddress } from '@/lib/launchpad/contracts'
import { adjustSimWalletWei, getSimWalletWei } from '@/lib/launchpad/store'
import { jsonToBigint, parseEthToWei } from '@/lib/launchpad/wei'
import { creatorPctFromLp, PRESALE_CREATOR_MAX_PCT, PRESALE_LAUNCH_FEE_ETH } from '@/lib/protocol'
import {
  canCancelPresale,
  canContribute,
  getPresaleStatus,
  pledgedByWallet,
  presaleWalletKey,
  remainingToHardCap,
  validateContribution,
  validateWithdrawal,
} from '@/lib/presale/math'
import type { CreateSimPresaleInput, SerializedSimPresale, SimPresale } from '@/lib/presale/types'
import { applyLiveListingReset, isAfterLiveReset } from '@/lib/liveReset'
import { persistedShareSlugs, uniqueShareSlug } from '@/lib/shareSlug'
import { assertCreatorAllowed, assertPenguinPadBrandAllowed } from '@/lib/createGuard'
import { isHiddenLaunch } from '@/lib/hiddenListings'
import { reportTradeActivity } from '@/lib/tradeActivity'

const PRESALES_KEY = 'penguin-pad-sim-presales-v2'
const STORE_EVENT = 'penguin-pad-sim-presale'

function canUseStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

function serializePresale(presale: SimPresale): SerializedSimPresale {
  const pledges: Record<string, string> = {}
  for (const [wallet, amount] of Object.entries(presale.pledges))
    pledges[wallet] = amount.toString()
  return {
    ...presale,
    softCapWei: presale.softCapWei.toString(),
    hardCapWei: presale.hardCapWei.toString(),
    minContributionWei: presale.minContributionWei.toString(),
    maxContributionWei: presale.maxContributionWei.toString(),
    tokensPerEthWei: (presale.tokensPerEthWei ?? 0n).toString(),
    listingRateWei: (presale.listingRateWei ?? presale.tokensPerEthWei ?? 0n).toString(),
    teamPct: Math.max(0, Math.min(PRESALE_CREATOR_MAX_PCT, Math.round(presale.teamPct ?? creatorPctFromLp(presale.lpPct)))),
    startDelaySeconds: presale.startDelaySeconds ?? 0,
    totalSupplyWei: (presale.totalSupplyWei ?? 0n).toString(),
    cancelled: Boolean(presale.cancelled),
    finalized: Boolean(presale.finalized),
    success: typeof presale.success === 'boolean' ? presale.success : undefined,
    raisedWei: presale.raisedWei.toString(),
    pledges,
  }
}

function deserializePresale(raw: SerializedSimPresale): SimPresale {
  const pledges: Record<string, bigint> = {}
  for (const [wallet, amount] of Object.entries(raw.pledges ?? {}))
    pledges[wallet] = jsonToBigint(amount)
  return {
    ...raw,
    softCapWei: jsonToBigint(raw.softCapWei),
    hardCapWei: jsonToBigint(raw.hardCapWei),
    minContributionWei: jsonToBigint(raw.minContributionWei),
    maxContributionWei: jsonToBigint(raw.maxContributionWei),
    tokensPerEthWei: jsonToBigint(raw.tokensPerEthWei),
    listingRateWei: jsonToBigint(raw.listingRateWei || raw.tokensPerEthWei),
    teamPct: Math.max(0, Math.min(PRESALE_CREATOR_MAX_PCT, Math.round(raw.teamPct ?? creatorPctFromLp(raw.lpPct)))),
    startDelaySeconds: Math.max(0, Math.round(raw.startDelaySeconds ?? 0)),
    totalSupplyWei: jsonToBigint(raw.totalSupplyWei),
    cancelled: Boolean(raw.cancelled),
    finalized: Boolean(raw.finalized),
    success: typeof raw.success === 'boolean' ? raw.success : undefined,
    raisedWei: jsonToBigint(raw.raisedWei),
    pledges,
  }
}

function readPresales(): SimPresale[] {
  applyLiveListingReset()
  if (!canUseStorage())
    return []
  try {
    const raw = window.localStorage.getItem(PRESALES_KEY)
    if (!raw)
      return []
    const parsed = JSON.parse(raw) as SerializedSimPresale[]
    return Array.isArray(parsed)
      ? parsed.map(deserializePresale).filter(presale => isAfterLiveReset(Date.parse(presale.createdAt) || 0) && !isHiddenLaunch(presale))
      : []
  }
  catch {
    return []
  }
}

function writePresales(next: SimPresale[]) {
  if (!canUseStorage())
    return
  try {
    window.localStorage.setItem(PRESALES_KEY, JSON.stringify(next.map(serializePresale)))
    window.dispatchEvent(new Event(STORE_EVENT))
  }
  catch {
    // Ignore quota / private-mode failures in this simulated UI.
  }
}

export function subscribeSimPresales(listener: () => void): () => void {
  if (typeof window === 'undefined')
    return () => {}
  const onStorage = (event: StorageEvent) => {
    if (event.key === PRESALES_KEY)
      listener()
  }
  window.addEventListener(STORE_EVENT, listener)
  window.addEventListener('storage', onStorage)
  return () => {
    window.removeEventListener(STORE_EVENT, listener)
    window.removeEventListener('storage', onStorage)
  }
}

export function listSimPresales(): SimPresale[] {
  return readPresales()
}

export function getSimPresale(id: string): SimPresale | undefined {
  const key = id.trim().toLowerCase()
  return readPresales().find((item) => {
    return item.id === id
      || item.id.toLowerCase() === key
      || item.contractAddress?.toLowerCase() === key
      || item.shareSlug?.toLowerCase() === key
  })
}

export function updateSimPresaleImage(id: string, imageUrl: string) {
  const nextUrl = imageUrl.trim()
  if (!id.trim() || !nextUrl)
    return
  const list = readPresales()
  const current = findPresale(list, id)
  if (!current || current.imageUrl === nextUrl)
    return
  writePresales(list.map(item => (item.id === current.id ? { ...current, imageUrl: nextUrl } : item)))
}

export function upsertOnChainPresale(presale: SimPresale) {
  const list = readPresales()
  const key = presale.contractAddress?.trim().toLowerCase()
  const index = list.findIndex((item) => {
    return item.id === presale.id
      || (key && item.id.toLowerCase() === key)
      || (key && item.contractAddress?.toLowerCase() === key)
  })
  const existingSlug = index >= 0 ? list[index]?.shareSlug : presale.shareSlug
  const shareSlug = uniqueShareSlug(presale.name, persistedShareSlugs(existingSlug), existingSlug)
  const nextPresale = { ...presale, shareSlug }
  const next = index >= 0
    ? list.map((item, i) => (i === index ? nextPresale : item))
    : [nextPresale, ...list]
  const currentRaw = JSON.stringify(list.map(serializePresale))
  const nextRaw = JSON.stringify(next.map(serializePresale))
  if (currentRaw === nextRaw)
    return
  writePresales(next)
}

export function createSimPresale(input: CreateSimPresaleInput): SimPresale {
  const creatorAddress = input.creatorAddress.trim()
  if (!creatorAddress)
    throw new Error('Connect a wallet before opening a presale')
  const chargeSimFee = input.chargeSimFee !== false
  const launchFeeWei = parseEthToWei(String(PRESALE_LAUNCH_FEE_ETH))
  if (chargeSimFee) {
    const wallet = getSimWalletWei(creatorAddress)
    if (wallet < launchFeeWei)
      throw new Error(`Sim wallet cannot cover the ${PRESALE_LAUNCH_FEE_ETH} ETH launch fee`)
  }

  const now = Date.now()
  const ticker = input.ticker.trim().toUpperCase()
  assertCreatorAllowed(creatorAddress)
  assertPenguinPadBrandAllowed(input.name, ticker, creatorAddress)
  const id = input.id?.trim() || `${ticker.toLowerCase()}-${now.toString(36)}`
  const delayMs = Math.max(0, Math.round(input.startDelaySeconds || 0)) * 1000
  const startsAtMs = input.startsAtUnix && input.startsAtUnix > 0n
    ? Number(input.startsAtUnix) * 1000
    : now + delayMs
  const endsAtMs = input.endsAtUnix && input.endsAtUnix > 0n
    ? Number(input.endsAtUnix) * 1000
    : startsAtMs + input.durationDays * 24 * 60 * 60 * 1000
  const teamPct = Math.max(0, Math.min(PRESALE_CREATOR_MAX_PCT, Math.round(input.teamPct ?? creatorPctFromLp(input.lpPct))))
  const presale: SimPresale = {
    id,
    name: input.name.trim(),
    ticker,
    category: input.category,
    tagline: input.tagline.trim(),
    description: input.description.trim(),
    imageUrl: input.imageUrl?.trim() || undefined,
    website: input.website,
    twitter: input.twitter,
    youtube: input.youtube,
    telegram: input.telegram,
    discord: input.discord,
    tiktok: input.tiktok,
    facebook: input.facebook,
    instagram: input.instagram,
    creatorAddress,
    contractAddress: input.contractAddress?.trim() || undefined,
    tokenAddress: input.tokenAddress?.trim() || undefined,
    shareSlug: uniqueShareSlug(input.name.trim(), persistedShareSlugs(input.shareSlug), input.shareSlug),
    createdAt: new Date(now).toISOString(),
    startsAt: new Date(startsAtMs).toISOString(),
    endsAt: new Date(endsAtMs).toISOString(),
    lpPct: input.lpPct,
    softCapWei: input.softCapWei,
    hardCapWei: input.hardCapWei,
    minContributionWei: input.minContributionWei,
    maxContributionWei: input.maxContributionWei,
    tokensPerEthWei: input.tokensPerEthWei,
    listingRateWei: input.listingRateWei && input.listingRateWei > 0n ? input.listingRateWei : input.tokensPerEthWei,
    teamPct,
    startDelaySeconds: Math.max(0, Math.round(input.startDelaySeconds || 0)),
    totalSupplyWei: input.totalSupplyWei,
    cancelled: false,
    finalized: false,
    raisedWei: 0n,
    participants: 0,
    pledges: {},
  }

  if (chargeSimFee)
    adjustSimWalletWei(creatorAddress, -launchFeeWei)
  writePresales([presale, ...readPresales().filter(item => item.id !== id)])
  return presale
}

function findPresale(list: SimPresale[], id: string): SimPresale | undefined {
  const key = id.trim().toLowerCase()
  return list.find((item) => {
    return item.id === id
      || item.id.toLowerCase() === key
      || item.contractAddress?.toLowerCase() === key
      || item.shareSlug?.toLowerCase() === key
  })
}

export function contributeSimPresale(
  id: string,
  amountWei: bigint,
  address?: string,
  options?: { chargeSimQuote?: boolean, skipValidation?: boolean, txHash?: string },
): SimPresale {
  if (!address?.trim())
    throw new Error('Connect a wallet before contributing')
  const list = readPresales()
  const current = findPresale(list, id)
  if (!current)
    throw new Error('Presale not found')
  if (!options?.skipValidation) {
    const error = validateContribution(current, address, amountWei)
    if (error)
      throw new Error(error)
  }
  const chargeSimQuote = options?.chargeSimQuote !== false
  if (chargeSimQuote) {
    const wallet = getSimWalletWei(address)
    if (amountWei > wallet)
      throw new Error('Sim wallet does not have enough ETH')
  }

  const key = presaleWalletKey(address)
  const pledged = pledgedByWallet(current, address)
  const pledges = { ...current.pledges, [key]: pledged + amountWei }
  const next: SimPresale = {
    ...current,
    raisedWei: current.raisedWei + amountWei,
    pledges,
    participants: Object.values(pledges).filter(value => value > 0n).length,
  }
  if (chargeSimQuote)
    adjustSimWalletWei(address, -amountWei)
  writePresales(list.map(item => (item.id === current.id ? next : item)))
  reportTradeActivity({
    projectKey: next.contractAddress || next.id,
    kind: 'presale',
    wallet: address,
    side: 'buy',
    tokenAmount: '0',
    quoteAmount: amountWei.toString(),
    txHash: options?.txHash,
  })
  return next
}

export function withdrawSimPresale(
  id: string,
  amountWei: bigint,
  address?: string,
  options?: { creditSimQuote?: boolean, skipValidation?: boolean, txHash?: string },
): SimPresale {
  if (!address?.trim())
    throw new Error('Connect a wallet before withdrawing')
  const list = readPresales()
  const current = findPresale(list, id)
  if (!current)
    throw new Error('Presale not found')
  if (!options?.skipValidation) {
    const error = validateWithdrawal(current, address, amountWei)
    if (error)
      throw new Error(error)
  }

  const key = presaleWalletKey(address)
  const pledged = pledgedByWallet(current, address)
  const remaining = pledged - amountWei
  const pledges = { ...current.pledges }
  if (remaining > 0n)
    pledges[key] = remaining
  else
    delete pledges[key]

  const next: SimPresale = {
    ...current,
    raisedWei: current.raisedWei - amountWei,
    pledges,
    participants: Object.values(pledges).filter(value => value > 0n).length,
  }
  if (options?.creditSimQuote !== false)
    adjustSimWalletWei(address, amountWei)
  writePresales(list.map(item => (item.id === current.id ? next : item)))
  reportTradeActivity({
    projectKey: next.contractAddress || next.id,
    kind: 'presale',
    wallet: address,
    side: 'sell',
    tokenAmount: '0',
    quoteAmount: amountWei.toString(),
    txHash: options?.txHash,
  })
  return next
}

export function cancelSimPresale(id: string, address?: string, options?: { skipValidation?: boolean }): SimPresale {
  const list = readPresales()
  const current = findPresale(list, id)
  if (!current)
    throw new Error('Presale not found')
  if (!options?.skipValidation && !canCancelPresale(current, address))
    throw new Error('Creators can cancel before start, or only with more than 24 hours left.')
  const next: SimPresale = { ...current, cancelled: true }
  writePresales(list.map(item => (item.id === current.id ? next : item)))
  return next
}

export function finalizeSimPresale(id: string, options?: { success?: boolean }): SimPresale {
  const list = readPresales()
  const current = findPresale(list, id)
  if (!current)
    throw new Error('Presale not found')
  const success = options?.success ?? (current.raisedWei >= current.softCapWei && !current.cancelled)
  const next: SimPresale = {
    ...current,
    finalized: true,
    success,
  }
  writePresales(list.map(item => (item.id === current.id ? next : item)))
  return next
}

export function listCreatedSimPresales(address?: string): SimPresale[] {
  if (!address)
    return []
  return readPresales().filter(item => sameAddress(item.creatorAddress, address))
}

export { canContribute, getPresaleStatus, remainingToHardCap }
